import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Row, Col, Space, Typography, Tag, Button, Modal, Input, Select,
  message, Alert, Statistic, Checkbox, Tooltip,
} from 'antd';
import { RocketOutlined, SendOutlined, ReloadOutlined, WhatsAppOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Text, Paragraph } = Typography;

// Templates conhecidos aprovados no WABA. Se precisar de outro, e so digitar
// o nome no campo custom que aparece quando escolhe "outro".
const TEMPLATE_OPTIONS = [
  {
    value: 'futezap_welcom_community',
    label: 'futezap_welcom_community — Convite pra Comunidade Futezap',
    hint: 'Body fixo, sem variáveis — mesmo texto pra todos',
    no_param: true,
  },
  {
    value: 'futezap_news_ranking',
    label: 'futezap_news_ranking — Anúncio do Ranking do time',
    hint: 'Body com {{1}} = primeiro nome do organizador',
  },
  {
    value: 'futezap_news_ratings',
    label: 'futezap_news_ratings — Avaliações pós-jogo (sem parâmetro)',
    hint: 'Body fixo, sem variáveis — mesmo texto pra todos',
    no_param: true,
  },
];

const DEFAULT_INSTANCE = 'instance-42-waba-futezap-organizer';

const OrganizerBroadcast = () => {
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [templateChoice, setTemplateChoice] = useState('futezap_news_ranking');
  const [customTemplate, setCustomTemplate] = useState('');
  const [instance, setInstance] = useState(DEFAULT_INSTANCE);
  const [ignoreCooldown, setIgnoreCooldown] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldownHours, setCooldownHours] = useState(24);
  const [lastResults, setLastResults] = useState(null);

  const templateName = templateChoice === '__custom__' ? customTemplate.trim() : templateChoice;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get('/monitoring/organizer-broadcast/candidates'),
        api.get('/monitoring/organizer-broadcast/history'),
      ]);
      setRows(c.data?.customers || []);
      setCooldownHours(c.data?.cooldown_hours || 24);
      setHistory(h.data?.broadcasts || []);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      String(r.game_name || '').toLowerCase().includes(s) ||
      String(r.phone || '').includes(s)
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    const total = rows.length;
    const withPhone = rows.filter(r => r.has_phone).length;
    const inCooldown = rows.filter(r => r.cooldown_active).length;
    return { total, withPhone, inCooldown };
  }, [rows]);

  const send = async () => {
    if (selectedIds.length === 0) return message.warning('Selecione ao menos 1 time');
    if (!templateName) return message.warning('Escolha ou digite o nome do template');
    setSending(true);
    try {
      // Se template escolhido tem no_param=true (body fixo sem {{1}}), envia
      // o flag pro backend nao mandar components/parameters (senao Meta rejeita).
      const currentTemplate = TEMPLATE_OPTIONS.find(t => t.value === templateChoice);
      const noParam = !!currentTemplate?.no_param;
      const payload = {
        customer_ids: selectedIds,
        template_name: templateName,
        instance_name: instance,
        ignore_cooldown: ignoreCooldown,
        no_param: noParam,
      };
      const r = await api.post('/monitoring/organizer-broadcast/send', payload);
      const ok = (r.data?.results || []).filter(x => x.ok).length;
      const fail = (r.data?.results || []).length - ok;
      setLastResults(r.data?.results || []);
      message.success(`${ok} enviados, ${fail} falhas`);
      setSelectedIds([]);
      setModalOpen(false);
      load();
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setSending(false); }
  };

  const columns = [
    {
      title: 'Time', dataIndex: 'game_name', width: 240,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer">
            {v || '(sem nome)'}
          </a>
          <Text type="secondary" style={{ fontSize: 11 }}>
            #{r.customer_id} · {r.phone || <Tag color="red">sem tel</Tag>}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Vai no {{1}} do template', dataIndex: 'template_param', width: 240,
      render: (v) => <Text strong>{v || <Text type="secondary">organizador</Text>}</Text>,
    },
    {
      title: 'Plano', dataIndex: 'subscription_type', width: 110,
      render: (v) => v === 'TRIAL'
        ? <Tag color="blue">TRIAL</Tag>
        : <Tag color="green">{v || 'ATIVO'}</Tag>,
    },
    {
      title: 'Ultimo envio', dataIndex: 'last_sent_at', width: 200,
      render: (v, r) => v ? (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{dayjs(v).fromNow()}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.last_template || '-'}</Text>
        </Space>
      ) : <Text type="secondary">nunca</Text>,
    },
    {
      title: 'Status', width: 120,
      render: (_, r) => {
        if (!r.has_phone) return <Tag color="red">sem telefone</Tag>;
        if (r.cooldown_active) return (
          <Tooltip title={`Ultimo envio ha ${r.hours_since_last_send}h. Cooldown ${cooldownHours}h.`}>
            <Tag color="orange">cooldown</Tag>
          </Tooltip>
        );
        return <Tag color="green">pronto</Tag>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys) => setSelectedIds(keys),
    getCheckboxProps: (r) => ({ disabled: !r.has_phone }),
  };

  const readyIds = filtered.filter(r => r.has_phone && (!r.cooldown_active || ignoreCooldown)).map(r => r.customer_id);
  const selectAllReadyFiltered = () => setSelectedIds(readyIds);

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <RocketOutlined /> Broadcast pra organizadores
          </Title>
          <Text type="secondary">
            Envia template WABA pra times <strong>ATIVOS com assinatura ATIVA</strong> (inclui TRIAL). O template deve ter <strong>1 parâmetro</strong> — o <strong>nome do time</strong> (o Futezap não armazena o nome do organizador).
          </Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
        </Col>
      </Row>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Times ativos" value={stats.total} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Com telefone" value={stats.withPhone} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Em cooldown" value={stats.inCooldown} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Selecionados" value={selectedIds.length} valueStyle={{ color: '#25D366' }} /></Card></Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle" style={{ rowGap: 12 }}>
          <Col xs={24} md={10}>
            <Text strong>Template WABA</Text>
            <Select
              value={templateChoice}
              onChange={setTemplateChoice}
              style={{ width: '100%', marginTop: 4 }}
              options={[
                ...TEMPLATE_OPTIONS.map(t => ({ value: t.value, label: t.label })),
                { value: '__custom__', label: '+ Outro template (digitar nome)' },
              ]}
            />
            {templateChoice === '__custom__' && (
              <Input
                placeholder="nome_do_template_aprovado_na_meta"
                value={customTemplate}
                onChange={e => setCustomTemplate(e.target.value)}
                style={{ marginTop: 8 }}
              />
            )}
            {templateChoice !== '__custom__' && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                {TEMPLATE_OPTIONS.find(t => t.value === templateChoice)?.hint}
              </Text>
            )}
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Instancia WABA</Text>
            <Input
              value={instance}
              onChange={e => setInstance(e.target.value)}
              style={{ marginTop: 4 }}
              placeholder={DEFAULT_INSTANCE}
            />
          </Col>
          <Col xs={24} md={6}>
            <Text strong>Cooldown</Text>
            <div style={{ marginTop: 4 }}>
              <Checkbox checked={ignoreCooldown} onChange={e => setIgnoreCooldown(e.target.checked)}>
                Ignorar cooldown de {cooldownHours}h
              </Checkbox>
            </div>
          </Col>
        </Row>
      </Card>

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <Col>
            <Space>
              <Input.Search
                placeholder="Filtrar por time, organizador ou telefone"
                allowClear
                onChange={e => setSearch(e.target.value)}
                style={{ width: 320 }}
              />
              <Button size="small" onClick={selectAllReadyFiltered} disabled={readyIds.length === 0}>
                Selecionar {readyIds.length} prontos
              </Button>
              {selectedIds.length > 0 && (
                <Button size="small" danger onClick={() => setSelectedIds([])}>Limpar seleção</Button>
              )}
            </Space>
          </Col>
          <Col>
            <Button
              type="primary" icon={<SendOutlined />} size="large"
              disabled={selectedIds.length === 0 || !templateName}
              onClick={() => setModalOpen(true)}
              style={{ background: '#25D366', borderColor: '#25D366', fontWeight: 700 }}
            >
              Enviar pra {selectedIds.length} time(s)
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="customer_id"
          rowSelection={rowSelection}
          pagination={{ pageSize: 30, showSizeChanger: true, showTotal: (t) => `${t} times` }}
          loading={loading}
          size="middle"
          scroll={{ x: 900 }}
        />
      </Card>

      {history.length > 0 && (
        <Card title="Histórico (últimas 30 campanhas)" style={{ marginTop: 16 }}>
          <Table
            size="small"
            dataSource={history}
            rowKey="id"
            pagination={false}
            columns={[
              { title: 'ID', dataIndex: 'id', width: 60 },
              { title: 'Quando', dataIndex: 'created_at', width: 160, render: v => dayjs(v).format('DD/MM/YYYY HH:mm') },
              { title: 'Por', dataIndex: 'created_by', width: 120 },
              { title: 'Template', dataIndex: 'template_name' },
              { title: 'Total', dataIndex: 'total_recipients', width: 80, align: 'right' },
              { title: 'Enviados', dataIndex: 'sent', width: 100, align: 'right',
                render: v => <Text style={{ color: '#52c41a' }}>{v}</Text> },
              { title: 'Falhas', dataIndex: 'failed', width: 90, align: 'right',
                render: v => v > 0 ? <Text style={{ color: '#f5222d' }}>{v}</Text> : v },
            ]}
          />
        </Card>
      )}

      {lastResults && lastResults.some(r => !r.ok) && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message={`${lastResults.filter(r => !r.ok).length} falhas no ultimo envio`}
          description={
            <ul style={{ marginBottom: 0 }}>
              {lastResults.filter(r => !r.ok).slice(0, 10).map(r => (
                <li key={r.cid}>#{r.cid} — {r.error}</li>
              ))}
            </ul>
          }
        />
      )}

      <Modal
        title={<span><WhatsAppOutlined /> Confirmar envio</span>}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={send}
        okText={`Enviar agora (${selectedIds.length})`}
        cancelText="Cancelar"
        confirmLoading={sending}
        okButtonProps={{
          style: { background: '#25D366', borderColor: '#25D366', fontWeight: 700 },
          icon: <SendOutlined />,
        }}
      >
        <Paragraph>
          Vai enviar o template <strong>{templateName || '(vazio)'}</strong> via instancia <code>{instance}</code>
          {' '}pra <strong>{selectedIds.length}</strong> organizador(es).
        </Paragraph>
        <Paragraph>
          Cada mensagem tem <strong>1 parâmetro</strong> = nome do time
          (fallback: <em>organizador</em>).
        </Paragraph>
        {ignoreCooldown && (
          <Alert type="warning" showIcon
            message={`Cooldown de ${cooldownHours}h desligado — pode enviar pra quem ja recebeu.`}
            style={{ marginBottom: 8 }}
          />
        )}
        <Alert type="info" showIcon
          message="O template precisa estar aprovado no Business Manager da Meta, com body contendo {{1}}."
        />
      </Modal>
    </div>
  );
};

export default OrganizerBroadcast;
